// src/pages/tender/ContractorDocuments.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";

const DOC_TYPES = [
  "Signed LOA / Work Order",
  "PAN Card",
  "GST Certificate",
  "Bank Details / Cancelled Cheque",
  "Contractor Undertaking",
  "Insurance / Labour Compliance",
  "Material Test Certificate",
  "Site Photo / Report",
  "Other Document",
];

export default function ContractorDocuments() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [tender, setTender] = useState(null);
  const [docs, setDocs] = useState([]);

  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login first.");
        navigate("/tender/login", { replace: true });
        return;
      }
      setUser(u);
    });

    return () => unsub();
  }, [auth, navigate]);

  const loadData = async () => {
    if (!user || !tenderId) return;

    try {
      setLoading(true);

      const tSnap = await getDoc(doc(db, "tenders", tenderId));

      if (!tSnap.exists()) {
        toast.error("Project not found.");
        navigate("/tender/contractor?section=documents");
        return;
      }

      const t = { id: tSnap.id, ...tSnap.data() };

      if (t.awardedVendorUid && t.awardedVendorUid !== user.uid) {
        toast.error("This project is not assigned to your login.");
        navigate("/tender/contractor?section=documents");
        return;
      }

      setTender(t);

      const dSnap = await getDocs(
        query(
          collection(db, "contractor_documents"),
          where("tenderId", "==", tenderId),
          where("vendorUid", "==", user.uid)
        )
      );

      const rows = [];
      dSnap.forEach((d) => rows.push({ id: d.id, ...d.data() }));

      rows.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );

      setDocs(rows);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenderId]);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select document file.");
      return;
    }

    try {
      setUploading(true);

      const safeName = file.name.replace(/\s+/g, "_");
      const path = `contractor_documents/${tenderId}/${user.uid}/${Date.now()}_${safeName}`;

      const sRef = ref(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);

      await addDoc(collection(db, "contractor_documents"), {
        tenderId,
        vendorUid: user.uid,

        projectName: tender?.title || tender?.projectName || "",
        pacsName: tender?.ownerCompany || tender?.pacsName || tender?.organization || "",
        district: tender?.city || tender?.district || "",

        docType,
        remarks,
        fileName: file.name,
        fileUrl: url,
        filePath: path,

        status: "SUBMITTED",
        pmcStatus: "PENDING_VERIFICATION",

        createdAt: serverTimestamp(),
      });

      toast.success("Document uploaded successfully.");

      setFile(null);
      setRemarks("");
      setDocType(DOC_TYPES[0]);

      await loadData();
    } catch (e) {
      console.error(e);
      toast.error("Document upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (loading || !tender) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="rounded-2xl bg-white px-8 py-5 shadow text-sm text-slate-600">
            Loading documents...
          </div>
        </div>
      </>
    );
  }

  const projectName = tender.title || tender.projectName || "Project";
  const pacsName = tender.ownerCompany || tender.pacsName || tender.organization || "—";
  const district = tender.city || tender.district || "—";

  return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 px-4 py-8">
        <div className="mx-auto max-w-6xl">
          <button
            type="button"
            onClick={() => navigate("/tender/contractor?section=documents")}
            className="mb-4 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-700 shadow-sm hover:bg-slate-50"
          >
            ← Back to Documents
          </button>

          <div className="rounded-[2rem] border border-emerald-100 bg-white shadow-xl overflow-hidden">
            <div className="bg-slate-950 px-6 py-6 text-white">
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-300">
                Contractor Documents
              </div>
              <h1 className="mt-2 text-2xl font-black">{projectName}</h1>
              <div className="mt-2 text-xs text-slate-300">
                {pacsName} • {district} • Tender ID: {tenderId}
              </div>
            </div>

            <div className="grid gap-5 p-6 md:grid-cols-3">
              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Document Type
                </label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold"
                >
                  {DOC_TYPES.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Remarks
                </label>
                <input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional remarks..."
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold"
                />
              </div>

              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Upload File
                </label>
                <input
                  type="file"
                  accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-xs"
                />
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                type="button"
                disabled={uploading}
                onClick={handleUpload}
                className="rounded-full bg-emerald-600 px-6 py-2 text-xs font-black text-white hover:bg-emerald-700 disabled:bg-slate-300"
              >
                {uploading ? "Uploading..." : "Upload Document"}
              </button>
            </div>

            <div className="px-6 pb-6">
              <div className="rounded-2xl border border-slate-300 overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse text-[11px]">
                  <thead>
                    <tr className="bg-slate-950 text-white">
                      <th className="border border-slate-400 px-3 py-3 text-center">Sr</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">Project</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">Document Type</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">File Name</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">Remarks</th>
                      <th className="border border-slate-400 px-3 py-3 text-center">PMC Status</th>
                      <th className="border border-slate-400 px-3 py-3 text-center">View</th>
                    </tr>
                  </thead>

                  <tbody>
                    {docs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="border border-slate-300 px-3 py-6 text-center text-slate-500"
                        >
                          No document uploaded yet.
                        </td>
                      </tr>
                    ) : (
                      docs.map((d, idx) => (
                        <tr key={d.id} className="hover:bg-emerald-50">
                          <td className="border border-slate-300 px-3 py-3 text-center font-black">
                            {idx + 1}
                          </td>
                          <td className="border border-slate-300 px-3 py-3">
                            <div className="font-black text-slate-900">
                              {d.projectName || projectName}
                            </div>
                            <div className="mt-1 text-[10px] text-slate-500">
                              {d.pacsName || pacsName}
                            </div>
                          </td>
                          <td className="border border-slate-300 px-3 py-3 font-semibold">
                            {d.docType || "—"}
                          </td>
                          <td className="border border-slate-300 px-3 py-3">
                            {d.fileName || "—"}
                          </td>
                          <td className="border border-slate-300 px-3 py-3">
                            {d.remarks || "—"}
                          </td>
                          <td className="border border-slate-300 px-3 py-3 text-center">
                            <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-black text-amber-700">
                              {d.pmcStatus || "PENDING_VERIFICATION"}
                            </span>
                          </td>
                          <td className="border border-slate-300 px-3 py-3 text-center">
                            <a
                              href={d.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-full bg-emerald-600 px-4 py-2 text-[10px] font-black text-white"
                            >
                              View
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}